/**
 * The way back in, with nothing to keep. D-25.
 *
 * This is what replaces "open the link in your email" as the root of the surface. The
 * requirement was that somebody who has exchanged documents can return at any time, and a
 * link cannot carry that: it lasts as long as the recipient keeps the message, so "anytime"
 * would have meant "until the inbox is tidied" and the remedy would have been asking a
 * representative - which is the friction the whole item exists to remove.
 *
 * ## Two things on this screen are security decisions, not copy
 *
 * **Nothing here can say whether we know the address.** `POST /guest/v1/sign-in` answers
 * 202 with an empty body either way, because a differing answer publishes the firm's
 * client list to anybody willing to iterate. The code screen this hands off to carries the
 * "if we have that address" wording - see `CodeScreenProps.addressConfirmed` - and this one
 * deliberately renders no confirmation of its own, because two of them would be two
 * chances to overstate it. It costs the person who mistypes their own address a wait for a
 * message that will not arrive, and that cost was accepted in D-25.
 *
 * **The address is kept in component state and sent again with the code.** Nothing is
 * stored between the two steps - no cookie, no `sessionStorage` - because a value that
 * says "this browser is midway through signing in as somebody" is a small credential of
 * its own, and one that would outlive the ten minutes the code is bounded by.
 */
import { useState } from 'react';
import { Button, Card, Field, Page } from '@iqz-ddds/components';
import { FailureNotice } from '../components/FailureNotice';

export interface SignInScreenProps {
  /** Resolves when the request has been accepted, which says nothing about the address. */
  readonly onRequestCode: (email: string) => Promise<void>;
}

/** Enough to catch a typo before a round trip. The server is the one that decides. */
function looksLikeAnAddress(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function SignInScreen({ onRequestCode }: SignInScreenProps): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  const submit = async (): Promise<void> => {
    if (!looksLikeAnAddress(email.trim())) {
      setFieldError('Enter an email address.');
      return;
    }

    setBusy(true);
    setError(null);
    setFieldError(undefined);
    try {
      // The caller moves to the code screen. This screen renders no confirmation of its
      // own, because two of them would be two chances to overstate what the server said.
      await onRequestCode(email.trim());
    } catch (err) {
      // Reaches here only for a transport failure or a rate limit. A refused *address* is
      // indistinguishable from an accepted one by design and never lands in this branch.
      setError(err instanceof Error ? err : new Error('Something went wrong.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page eyebrow="Secure documents" title="Send and receive documents">
      <FailureNotice error={error} />

      <Card>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <Field
            id="email"
            label="Your email address"
            hint="The address your adviser uses for you. We will send a six-digit code to it."
            value={email}
            onChange={(value) => {
              setEmail(value);
              setFieldError(undefined);
            }}
            inputMode="email"
            // Lets a phone fill it from the keychain, which is most of the reason this is
            // less friction than finding the original email.
            autoComplete="email"
            maxLength={320}
            disabled={busy}
            {...(fieldError === undefined ? {} : { error: fieldError })}
          />

          <Button type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Send me a code'}
          </Button>
        </form>
      </Card>

      <p className="ddds-footer">
        No password, and nothing to create. The code proves you can read the mailbox, which is
        all we need.
      </p>
    </Page>
  );
}
